// Utility for bridging web <-> native (ReactNativeWebView)
// Use for share, download, etc. Fallbacks to browser if not in WebView.

export function triggerNativeShare({
  title,
  fileKey,
  filename,
}: {
  title: string;
  fileKey: string;
  filename?: string;
}) {
  // Extract filename from fileKey if not provided
  const extractedFilename = filename || extractFilenameFromFileKey(fileKey);

  if (typeof window !== "undefined" && (window as any).ReactNativeWebView) {
    (window as any).ReactNativeWebView.postMessage(
      JSON.stringify({
        type: "share",
        payload: { title, fileKey, filename: extractedFilename },
      })
    );
  }
}

// Helper function to extract filename from fileKey
function extractFilenameFromFileKey(fileKey: string): string {
  // fileKey format: "memberId/uuid_filename.pdf"
  const parts = fileKey.split("/");
  return parts[parts.length - 1]; // Returns "uuid_filename.pdf"
}

export function triggerNativeEmergencyShare({
  memberId,
  memberName,
}: {
  memberId: string;
  memberName: string;
}) {
  if (typeof window !== "undefined" && (window as any).ReactNativeWebView) {
    (window as any).ReactNativeWebView.postMessage(
      JSON.stringify({
        type: "emergency_share",
        payload: { memberId: memberId, memberName: memberName },
      })
    );
  }
}

export function triggerNativeWalletCardShare({
  memberId,
  memberName,
}: {
  memberId: string;
  memberName: string;
}) {
  if (typeof window !== "undefined" && (window as any).ReactNativeWebView) {
    (window as any).ReactNativeWebView.postMessage(
      JSON.stringify({
        type: "wallet_card_share",
        payload: { memberId: memberId, memberName: memberName },
      })
    );
  }
}

export function triggerNativeHealthSummaryShare({
  memberId,
  memberName,
}: {
  memberId: string;
  memberName: string;
}) {
  if (typeof window !== "undefined" && (window as any).ReactNativeWebView) {
    (window as any).ReactNativeWebView.postMessage(
      JSON.stringify({
        type: "health_summary_share",
        payload: { memberId: memberId, memberName: memberName },
      })
    );
  }
}

/**
 * Ask the native shell to fetch the lab-insights parameter PDF and present
 * the system share sheet — same pattern as `triggerNativeEmergencyShare` and
 * `triggerNativeHealthSummaryShare`. Native side calls
 *   GET /lab-interpretations/:memberId/parameters/:canonicalParameterId/pdf
 * to get the buffer and hands it to the OS share intent.
 */
export function triggerNativeLabInsightsParameterShare({
  memberId,
  memberName,
  canonicalParameterId,
  parameterName,
}: {
  memberId: string;
  memberName: string;
  canonicalParameterId: string;
  parameterName: string;
}): boolean {
  if (typeof window !== "undefined" && (window as any).ReactNativeWebView) {
    (window as any).ReactNativeWebView.postMessage(
      JSON.stringify({
        type: "lab_insights_parameter_share",
        payload: {
          memberId,
          memberName,
          canonicalParameterId,
          parameterName,
        },
      })
    );
    return true;
  }
  return false;
}

export function triggerNativeDownload({
  fileKey,
  filename,
}: {
  fileKey: string;
  filename: string;
}) {
  if (typeof window !== "undefined" && (window as any).ReactNativeWebView) {
    (window as any).ReactNativeWebView.postMessage(
      JSON.stringify({
        type: "download",
        payload: { fileKey, filename },
      })
    );
  }
}

// Send login tokens directly to React Native WebView
export function triggerNativeLogin(token: string, member: any) {
  if (typeof window !== "undefined" && (window as any).ReactNativeWebView) {
    (window as any).ReactNativeWebView.postMessage(
      JSON.stringify({ type: "login", payload: { token, member } })
    );
  }
}

// Send logout event to React Native WebView
export function triggerNativeLogout() {
  if (typeof window !== "undefined" && (window as any).ReactNativeWebView) {
    (window as any).ReactNativeWebView.postMessage(
      JSON.stringify({ type: "logout" })
    );
  }
}

// Send offline document clear event to React Native WebView
export function triggerNativeOfflineDoc(payload?: any) {
  if (typeof window !== "undefined" && (window as any).ReactNativeWebView) {
    (window as any).ReactNativeWebView.postMessage(
      JSON.stringify({ type: "offline_doc", payload })
    );
  }
}

// Trigger native camera capture
export function triggerNativeCameraCapture() {
  if (typeof window !== "undefined" && (window as any).ReactNativeWebView) {
    (window as any).ReactNativeWebView.postMessage(
      JSON.stringify({
        type: "document_upload",
        payload: { source: "camera" },
      })
    );
  }
}

/**
 * Ask the native shell to fetch the vitals PDF and present the OS share sheet.
 * Native side calls GET /vitals/pdf/:memberId/:parameter to get the buffer
 * and hands it to the OS share intent.
 * Returns true if the message was dispatched (i.e. we are inside a WebView).
 */
export function triggerNativeVitalsShare({
  memberId,
  memberName,
  parameter,
  parameterName,
  startDate,
  endDate,
  timezone,
}: {
  memberId: string;
  memberName: string;
  parameter: string;
  parameterName: string;
  startDate?: string;
  endDate?: string;
  timezone?: string;
}): boolean {
  if (typeof window !== "undefined" && (window as any).ReactNativeWebView) {
    (window as any).ReactNativeWebView.postMessage(
      JSON.stringify({
        type: "vitals_share",
        payload: {
          memberId,
          memberName,
          parameter,
          parameterName,
          startDate,
          endDate,
          timezone,
        },
      })
    );
    return true;
  }
  return false;
}

// Trigger native gallery picker
export function triggerNativeGalleryPick() {
  if (typeof window !== "undefined" && (window as any).ReactNativeWebView) {
    (window as any).ReactNativeWebView.postMessage(
      JSON.stringify({
        type: "document_upload",
        payload: { source: "gallery" },
      })
    );
  }
}

// Trigger native file picker
export function triggerNativeFilePick() {
  if (typeof window !== "undefined" && (window as any).ReactNativeWebView) {
    (window as any).ReactNativeWebView.postMessage(
      JSON.stringify({
        type: "document_upload",
        payload: { source: "files" },
      })
    );
  }
}

// Google Sign-In trigger for React Native WebView
export function triggerNativeGoogleSignIn() {
  if (
    typeof window !== "undefined" &&
    (window as any).ReactNativeWebView &&
    (window as any).ReactNativeWebView.postMessage
  ) {
    (window as any).ReactNativeWebView.postMessage(
      JSON.stringify({ type: "google_signin" })
    );
  }
}

// Generic reminder event trigger for React Native WebView
export function triggerNativeReminder(
  action: "CREATE" | "UPDATE" | "DELETE",
  reminder: any
) {
  if (typeof window !== "undefined" && (window as any).ReactNativeWebView) {
    (window as any).ReactNativeWebView.postMessage(
      JSON.stringify({
        type: "reminders",
        payload: { action, reminder },
      })
    );
  }
}
// Optionally extend for more actions (camera, etc) as needed

// Notify React Native WebView of profile change
export function triggerNativeProfileChange({ memberId }: { memberId: string }) {
  if (typeof window !== "undefined" && (window as any).ReactNativeWebView) {
    (window as any).ReactNativeWebView.postMessage(
      JSON.stringify({ type: "profile_change", payload: { memberId } })
    );
  }
}

// Trigger native in-app purchase flow
export function triggerNativeIAPPurchase(payload: {
  memberId: string;
  familyId: string;
  planId: string;
  planName: string;
  planType: string;
  productId: string;
  price: number;
  currency: string;
}) {
  if (typeof window !== "undefined" && (window as any).ReactNativeWebView) {
    (window as any).ReactNativeWebView.postMessage(
      JSON.stringify({
        type: "iap_purchase",
        payload,
      })
    );
  }
}

// Trigger native subscription cancellation (opens App Store/Play Store subscription management)
export function triggerNativeIAPCancel() {
  if (typeof window !== "undefined" && (window as any).ReactNativeWebView) {
    (window as any).ReactNativeWebView.postMessage(
      JSON.stringify({
        type: "iap_cancel",
      })
    );
  }
}

/**
 * Tell the native KeyboardAvoidingView whether to avoid the keyboard.
 * Pass false when a drawer/form handles its own layout (keyboard shouldn't push the view).
 * Pass true to restore default behavior for normal page inputs.
 */
export function setNativeKeyboardAvoid(value: boolean) {
  if (typeof window !== "undefined" && (window as any).ReactNativeWebView) {
    (window as any).ReactNativeWebView.postMessage(
      JSON.stringify({ type: "set_keyboard_avoid", value })
    );
  }
}
// Request the installed native app version from React Native WebView.
// `version` / `buildNumber` are read on the native side from the signed binary
// (Info.plist CFBundleShortVersionString / AndroidManifest versionName), so the
// value reflects what the user actually installed from the store — not the
// value in app.config.js.
export type NativeAppVersionInfo = {
  version: string;
  buildNumber?: string;
  platform?: string;
};

export function requestNativeAppVersion(
  timeoutMs = 1500
): Promise<NativeAppVersionInfo | null> {
  return new Promise((resolve) => {
    if (typeof window === "undefined" || !(window as any).ReactNativeWebView) {
      resolve(null);
      return;
    }

    let timer: ReturnType<typeof setTimeout> | null = null;

    const cleanup = () => {
      window.removeEventListener("message", handler as EventListener);
      document.removeEventListener("message", handler as EventListener);
      if (timer) {
        clearTimeout(timer);
        timer = null;
      }
    };

    const handler = (event: MessageEvent) => {
      try {
        const raw = typeof event.data === "string" ? event.data : "";
        if (!raw) return;
        const data = JSON.parse(raw);
        if (data && data.type === "app_version" && data.payload) {
          cleanup();
          resolve({
            version: String(data.payload.version || ""),
            buildNumber: data.payload.buildNumber,
            platform: data.payload.platform,
          });
        }
      } catch {
        // Ignore non-JSON messages
      }
    };

    window.addEventListener("message", handler as EventListener);
    // Android RN WebView dispatches message events on document
    document.addEventListener("message", handler as EventListener);

    timer = setTimeout(() => {
      cleanup();
      resolve(null);
    }, timeoutMs);

    (window as any).ReactNativeWebView.postMessage(
      JSON.stringify({ type: "get_app_version" })
    );
  });
}
